import type {SplineInterpolationPointLike} from './normalizeSplineInterpolationPoints';

/**
 * 开放插值样条轻量采样点。
 */
export interface OpenSplineInterpolationPoint {
    /** X 坐标。 */
    x: number;
    /** Y 坐标。 */
    y: number;
    /** Z 坐标。 */
    z: number;
}

/** Catmull-Rom 切线缩放系数（相邻点距离差的一半）。 */
const CATMULL_ROM_TANGENT_SCALE = 0.5;

/**
 * 计算内部插值点的 Catmull-Rom 切线。
 * @param previousPoint 前一个插值点。
 * @param nextPoint 后一个插值点。
 * @returns 内部点切线向量。
 */
function computeCatmullRomTangent(
    previousPoint: SplineInterpolationPointLike,
    nextPoint: SplineInterpolationPointLike,
): OpenSplineInterpolationPoint {
    return {
        x: (nextPoint.x - previousPoint.x) * CATMULL_ROM_TANGENT_SCALE,
        y: (nextPoint.y - previousPoint.y) * CATMULL_ROM_TANGENT_SCALE,
        z: (nextPoint.z - previousPoint.z) * CATMULL_ROM_TANGENT_SCALE,
    };
}

/**
 * 计算端点切线（单侧差分）。
 * @param endPoint 端点。
 * @param neighborPoint 相邻插值点。
 * @returns 端点切线向量。
 */
function computeEndpointTangent(
    endPoint: SplineInterpolationPointLike,
    neighborPoint: SplineInterpolationPointLike,
): OpenSplineInterpolationPoint {
    return {
        x: neighborPoint.x - endPoint.x,
        y: neighborPoint.y - endPoint.y,
        z: neighborPoint.z - endPoint.z,
    };
}

/**
 * 按三次 Hermite 基函数计算单个采样点。
 * @param startPoint 段起点。
 * @param startTangent 段起点切线。
 * @param endPoint 段终点。
 * @param endTangent 段终点切线。
 * @param parameter 段内参数，范围 [0, 1]。
 * @returns 采样点。
 */
function sampleHermiteSegmentPoint(
    startPoint: SplineInterpolationPointLike,
    startTangent: OpenSplineInterpolationPoint,
    endPoint: SplineInterpolationPointLike,
    endTangent: OpenSplineInterpolationPoint,
    parameter: number,
): OpenSplineInterpolationPoint {
    const t2 = parameter * parameter;
    const t3 = t2 * parameter;
    // 三次 Hermite 基函数：
    // h00 = 2t³ - 3t² + 1（起点权重）、h10 = t³ - 2t² + t（起点切线权重）、
    // h01 = -2t³ + 3t²（终点权重）、h11 = t³ - t²（终点切线权重）。
    const h00 = 2 * t3 - 3 * t2 + 1;
    const h10 = t3 - 2 * t2 + parameter;
    const h01 = -2 * t3 + 3 * t2;
    const h11 = t3 - t2;

    return {
        x: h00 * startPoint.x + h10 * startTangent.x + h01 * endPoint.x + h11 * endTangent.x,
        y: h00 * startPoint.y + h10 * startTangent.y + h01 * endPoint.y + h11 * endTangent.y,
        z: h00 * startPoint.z + h10 * startTangent.z + h01 * endPoint.z + h11 * endTangent.z,
    };
}

/**
 * 分配每个样条区段的采样段数。
 * 说明：
 * 1. 每个区段起点对应一个用户插值点，按区段分配可保证折线点列包含所有插值点；
 * 2. 余数优先分配到前面的区段，保证总采样段数与调用方预算一致。
 * @param segmentCount 总采样段数。
 * @param spanCount 区段数量（= 插值点数 - 1）。
 * @returns 每个区段的采样段数。
 */
function allocateSplineSpanSegmentCounts(segmentCount: number, spanCount: number): number[] {
    const baseSegmentCount = Math.floor(segmentCount / spanCount);
    const remainderSegmentCount = segmentCount % spanCount;

    return Array.from({length: spanCount}, (_unused, index) => (
        baseSegmentCount + (index < remainderSegmentCount ? 1 : 0)
    ));
}

/**
 * 采样开放插值样条（CPU 轻量实现）。
 *
 * 设计说明：
 * 1. 使用均匀参数化的 Catmull-Rom 样条，穿过所有插值点且逐段三次 Hermite 平滑；
 * 2. 该实现只服务拖拽期实时预览，视觉近似即可；松手后的最终几何仍由
 *    Worker（Puppy CAD 内核 插值）精确重建，两者结果会自然收敛；
 * 3. 少于 3 个插值点时退化为折线，避免切线计算越界。
 *
 * @param points 插值点列表，至少 2 个。
 * @param segments 总采样段数。
 * @returns 曲线采样点列表，首尾与输入插值点一致。
 */
export function sampleOpenSplineInterpolationPoints(
    points: readonly SplineInterpolationPointLike[],
    segments: number,
): OpenSplineInterpolationPoint[] {
    if (points.length < 2) {
        return points.map((point) => ({x: point.x, y: point.y, z: point.z}));
    }
    if (points.length === 2) {
        // 两个点只能构成直线段，直接线性插值。
        return Array.from({length: Math.max(1, segments) + 1}, (_unused, index) => {
            const parameter = index / Math.max(1, segments);
            return {
                x: points[0].x + (points[1].x - points[0].x) * parameter,
                y: points[0].y + (points[1].y - points[0].y) * parameter,
                z: points[0].z + (points[1].z - points[0].z) * parameter,
            };
        });
    }

    // 预计算每个插值点的切线：端点用单侧差分，内部点用相邻点差分的一半。
    const tangents: OpenSplineInterpolationPoint[] = points.map((point, index) => {
        if (index === 0) {
            return computeEndpointTangent(point, points[1]);
        }
        if (index === points.length - 1) {
            // 终点切线必须从相邻点指向端点（曲线到达端点时的前进方向）；
            // 方向写反会让曲线末端冲出端点再 180° 折返，形成"倒钩"。
            return computeEndpointTangent(points[points.length - 2], point);
        }
        return computeCatmullRomTangent(points[index - 1], points[index + 1]);
    });

    const segmentCount = Math.max(points.length - 1, Math.floor(segments));
    const spanSegmentCounts = allocateSplineSpanSegmentCounts(segmentCount, points.length - 1);
    const sampledPoints: OpenSplineInterpolationPoint[] = [];

    for (let spanIndex = 0; spanIndex < spanSegmentCounts.length; spanIndex += 1) {
        const currentSpanSegmentCount = spanSegmentCounts[spanIndex];
        const startPoint = points[spanIndex];
        const endPoint = points[spanIndex + 1];

        // 每个区段从参数 0 开始采样，因此用户插值点会稳定出现在采样点列里。
        for (let segmentIndex = 0; segmentIndex < currentSpanSegmentCount; segmentIndex += 1) {
            sampledPoints.push(sampleHermiteSegmentPoint(
                startPoint,
                tangents[spanIndex],
                endPoint,
                tangents[spanIndex + 1],
                segmentIndex / currentSpanSegmentCount,
            ));
        }
    }

    // 显式追加末点，保证采样点列包含最后一个插值点。
    sampledPoints.push({
        x: points[points.length - 1].x,
        y: points[points.length - 1].y,
        z: points[points.length - 1].z,
    });
    return sampledPoints;
}
